import type { ApiBody, ApiResponse } from '../../api/contract';

export type WalletSummary = ApiResponse<'/api/v1/affiliate-wallets', 'get'>['data'][number];
export type WalletDetail = ApiResponse<'/api/v1/affiliate-wallets/{id}', 'get'>['data'];
export type WalletLedgerEntry = ApiResponse<
  '/api/v1/affiliate-wallets/{id}/entries',
  'get'
>['data'][number];
export type WalletStatus = WalletSummary['status'];
export type WalletReconciliationStatus = WalletSummary['reconciliation']['status'];
export type WalletEntryType = WalletLedgerEntry['entryType'];
export type WalletAdjustmentInput = ApiBody<
  '/api/v1/affiliate-wallets/{id}/adjustments/preview',
  'post'
>;
export type CreateWalletAdjustmentInput = ApiBody<
  '/api/v1/affiliate-wallets/{id}/adjustments',
  'post'
>;
export type WalletAdjustmentPreview = ApiResponse<
  '/api/v1/affiliate-wallets/{id}/adjustments/preview',
  'post'
>['data'];
export type ApprovalRequestSummary = ApiResponse<
  '/api/v1/affiliate-wallets/{id}/adjustments',
  'post'
>['data'];

export interface WalletListFilters {
  readonly partnerId: string;
  readonly agentId: string;
  readonly memberId: string;
  readonly username: string;
  readonly partnerUserId: string;
  readonly currency: string;
  readonly status: WalletStatus | '';
  readonly reconciliationStatus: WalletReconciliationStatus | '';
  readonly minimumAvailableBalance: string;
  readonly maximumAvailableBalance: string;
  readonly minimumReservedBalance: string;
  readonly maximumReservedBalance: string;
}

export interface WalletListQuery {
  readonly cursor: string;
  readonly pageSize: number;
  readonly sort: 'updatedAt' | 'createdAt';
  readonly filters: WalletListFilters;
}

export interface WalletEntryFilters {
  readonly entryType: WalletEntryType | '';
  readonly sourceTransactionId: string;
  readonly sourceWithdrawalId: string;
  readonly sourceAdjustmentId: string;
  readonly minimumAmount: string;
  readonly maximumAmount: string;
  readonly effectiveFrom: string;
  readonly effectiveTo: string;
}

export interface WalletEntryQuery {
  readonly cursor: string;
  readonly pageSize: number;
  readonly sort: 'effectiveAt' | 'createdAt';
  readonly filters: WalletEntryFilters;
}

export interface WalletStatusInput {
  readonly status: 'ACTIVE' | 'SUSPENDED';
  readonly reason: string;
}

/** Formats a server decimal exactly without first converting it to binary floating point. */
export function formatWalletMoney(amount: string, currency: string, minorUnit: number): string {
  const match = /^(-)?(0|[1-9][0-9]*)(?:\.([0-9]+))?$/u.exec(amount.trim());
  const currencyCode = currency.trim().toUpperCase();
  if (match === null) return `${currencyCode}\u00a0${amount}`;
  const negative = match[1] === '-';
  const whole = match[2] ?? '0';
  const fraction = match[3] ?? '';
  const scale = Math.max(0, Math.min(6, minorUnit));
  const factor = 10n ** BigInt(scale);
  const padded = fraction.padEnd(scale + 1, '0');
  let units = BigInt(whole) * factor + BigInt(padded.slice(0, scale) || '0');
  if ((padded[scale] ?? '0') >= '5') units += 1n;
  const roundedWhole = units / factor;
  const roundedFraction = scale === 0 ? '' : String(units % factor).padStart(scale, '0');
  const grouped = groupThousands(String(roundedWhole));
  return `${negative && units !== 0n ? '-' : ''}${currencyCode}\u00a0${grouped}${
    scale === 0 ? '' : `.${roundedFraction}`
  }`;
}

export function compareWalletDecimals(left: string, right: string): number {
  const a = decimalParts(left);
  const b = decimalParts(right);
  const scale = Math.max(a.scale, b.scale);
  const leftUnits = signedUnits(a, scale);
  const rightUnits = signedUnits(b, scale);
  return leftUnits < rightUnits ? -1 : leftUnits > rightUnits ? 1 : 0;
}

function groupThousands(value: string): string {
  let grouped = '';
  for (let index = 0; index < value.length; index += 1) {
    if (index > 0 && (value.length - index) % 3 === 0) grouped += ',';
    grouped += value[index];
  }
  return grouped;
}

function decimalParts(value: string) {
  const match = /^(-)?(0|[1-9][0-9]*)(?:\.([0-9]+))?$/u.exec(value.trim());
  if (match === null) throw new Error('Expected a canonical decimal string');
  const fraction = match[3] ?? '';
  return {
    negative: match[1] === '-',
    units: BigInt(`${match[2] ?? '0'}${fraction}`),
    scale: fraction.length,
  };
}

function signedUnits(
  value: { readonly negative: boolean; readonly scale: number; readonly units: bigint },
  scale: number,
) {
  const units = value.units * 10n ** BigInt(scale - value.scale);
  return value.negative ? -units : units;
}
